import { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ocrService } from '../../services';
import { createLogger } from '../../services/Debug';
export function useOcrProcessor({
  isOnline,
  showNotification,
  setAnnexText,
  onOcrComplete,
  onOcrError = null,
  selectedReportType = '', // Add selectedReportType parameter
  currentDraftId = null, // Add currentDraftId parameter
  isGlobalAirplaneMode = false // Add airplane mode parameter
}) {
  //OCR
  const log = createLogger('useOcrProcessor');
  const [ocrQueue, setOcrQueue] = useState([]);
  const ocrQueueRef = useRef(ocrQueue);
  ocrQueueRef.current = ocrQueue;
  const ocrProcessingRef = useRef(false);
  const [ocrRetryCount, setOcrRetryCount] = useState({});
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [ocrErrorDialogState, setOcrErrorDialogState] = useState(null);
  const ocrErrorDialogStateRef = useRef(null); // Ref to avoid circular dependency in processOcrQueue
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });

  // Store file blobs in memory keyed by file ID
  const ocrFileBlobsRef = useRef(new Map());

  // Track current draft ID for file management
  const currentDraftIdRef = useRef(currentDraftId);
  currentDraftIdRef.current = currentDraftId;

  // Track if queue was paused due to airplane mode
  const [pausedDueToAirplaneMode, setPausedDueToAirplaneMode] = useState(false);

  // Create a ref to store processOcrQueue function to avoid circular dependencies
  const processOcrQueueRef = useRef(null);

  // Save OCR queue to localStorage for a specific draft
  const saveOcrQueueForDraft = useCallback((draftId) => {
    if (!draftId) return;
    try {
      const queueMetadata = ocrQueueRef.current.map(file => ({
        id: file.name + '_' + file.lastModified, // Create unique ID for file
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      }));
      localStorage.setItem(`ocrQueue_${draftId}`, JSON.stringify(queueMetadata));
      log.info(`Saved OCR queue for draft ${draftId}:`, queueMetadata.length, 'files');
    } catch (e) {
      log.error('Failed to save OCR queue:', e);
    }
  }, []);

  // Load OCR queue from localStorage for a specific draft
  const loadOcrQueueForDraft = useCallback((draftId) => {
    if (!draftId) {
      // Clear if no draft
      setOcrQueue([]);
      ocrFileBlobsRef.current.clear();
      return;
    }
    try {
      const stored = localStorage.getItem(`ocrQueue_${draftId}`);
      if (stored) {
        const queueMetadata = JSON.parse(stored);
        // Restore files from memory if available
        const restoredQueue = queueMetadata
          .map(meta => ocrFileBlobsRef.current.get(meta.id))
          .filter(Boolean); // Remove nulls for files that are no longer in memory
        setOcrQueue(restoredQueue);
        log.info(`Loaded OCR queue for draft ${draftId}:`, restoredQueue.length, 'files');
      } else {
        setOcrQueue([]);
      }
    } catch (e) {
      log.error('Failed to load OCR queue:', e);
      setOcrQueue([]);
    }
  }, []);

  // Clear OCR queue for a specific draft
  const clearOcrQueueForDraft = useCallback((draftId) => {
    if (!draftId) return;
    try {
      localStorage.removeItem(`ocrQueue_${draftId}`);
      // Also remove blobs from memory for this draft
      const queue = ocrQueueRef.current;
      queue.forEach(file => {
        const fileId = file.name + '_' + file.lastModified;
        ocrFileBlobsRef.current.delete(fileId);
      });
      log.info(`Cleared OCR queue for draft ${draftId}`);
    } catch (e) {
      log.error('Failed to clear OCR queue:', e);
    }
  }, []);

  const addFileToOcrQueue = useCallback((file) => {
    // Store the file in memory
    const fileId = file.name + '_' + file.lastModified;
    ocrFileBlobsRef.current.set(fileId, file);

    setOcrQueue((prevQueue) => {
      const newQueue = [...prevQueue, file];
      // Si rien ne tourne et pas en mode avion, on lance le process
      if (!ocrProcessingRef.current && !isGlobalAirplaneMode && processOcrQueueRef.current) {
        // Pass the current selectedReportType to ensure DocumentAI is triggered when needed
        processOcrQueueRef.current(newQueue, selectedReportType);
      }
      return newQueue;
    });

    // Save to localStorage for current draft
    if (currentDraftIdRef.current) {
      setTimeout(() => saveOcrQueueForDraft(currentDraftIdRef.current), 100);
    }

    // If in airplane mode, show notification
    if (isGlobalAirplaneMode) {
      showNotification('Fichier ajouté. Annexe en pause (mode avion activé).', 'info');
    }
  }, [selectedReportType, isGlobalAirplaneMode, showNotification, saveOcrQueueForDraft]);

  // Handle OCR error by showing dialog
  const handleOcrError = useCallback((file, error, fileIndex) => {
    // Determine user-friendly error message
    let errorMessage = 'Une erreur est survenue lors de l\'extraction du texte.';
    
    if (!isOnline) {
      errorMessage = 'Pas de connexion internet. Impossible d\'extraire le texte.';
    } else if (error.message?.includes('quota')) {
      errorMessage = 'Limite quotidienne Google Vision atteinte.';
    } else if (error.message?.includes('API key')) {
      errorMessage = 'Clé API Google Vision invalide ou manquante.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'La requête a expiré. Veuillez vérifier votre connexion.';
    } else if (error.message?.includes('Type de fichier non géré')) {
      errorMessage = error.message;
    } else if (error.message?.includes('Aucun texte')) {
      errorMessage = 'Aucun texte n\'a pu être extrait de ce fichier.';
    } else if (error.message?.includes('Fichier texte vide')) {
      errorMessage = 'Le fichier est vide.';
    } else if (error.message?.includes('Document AI')) {
      errorMessage = error.message;
    }
    
    // Set error dialog state
    const errorState = {
      file,
      errorMessage,
      itemDescription: file.name,
      error
    };
    setOcrErrorDialogState(errorState);
    ocrErrorDialogStateRef.current = errorState;
    
    // Call parent error handler if provided
    if (onOcrError) {
      onOcrError({
        file,
        error,
        fileIndex,
        onRetry: () => handleOcrErrorRetry(file),
        onDelete: () => handleOcrErrorDelete(file)
      });
    }
  }, [isOnline, onOcrError]);

  // Categorize error type
  const categorizeError = useCallback((error) => {
    const errorMessage = error.message || error.toString();
    
    if (!isOnline) return 'NETWORK_ERROR';
    if (errorMessage.includes('quota')) return 'QUOTA_EXCEEDED';
    if (errorMessage.includes('API key')) return 'API_KEY_ERROR';
    if (errorMessage.includes('timeout')) return 'TIMEOUT';
    if (errorMessage.includes('Type de fichier non géré')) return 'UNSUPPORTED_TYPE';
    if (errorMessage.includes('Aucun texte')) return 'NO_TEXT_FOUND';
    if (errorMessage.includes('vide')) return 'EMPTY_FILE';
    if (errorMessage.includes('Document AI')) return 'DOCUMENT_AI_ERROR';
    
    return 'UNKNOWN_ERROR';
  }, [isOnline]);

  // Generate batch error message
  const generateBatchErrorMessage = useCallback((errorGroups) => {
    const messages = [];
    
    if (errorGroups.NETWORK_ERROR) {
      messages.push('Pas de connexion internet');
    }
    if (errorGroups.QUOTA_EXCEEDED) {
      messages.push('Limite quotidienne atteinte');
    }
    if (errorGroups.DOCUMENT_AI_ERROR) {
      messages.push('Erreur Document AI');
    }
    if (errorGroups.UNSUPPORTED_TYPE) {
      messages.push(`${errorGroups.UNSUPPORTED_TYPE.length} fichier(s) non supporté(s)`);
    }
    if (errorGroups.NO_TEXT_FOUND) {
      messages.push(`${errorGroups.NO_TEXT_FOUND.length} fichier(s) sans texte détecté`);
    }
    if (errorGroups.EMPTY_FILE) {
      messages.push(`${errorGroups.EMPTY_FILE.length} fichier(s) vide(s)`);
    }
    
    const otherErrors = Object.keys(errorGroups).filter(
      key => !['NETWORK_ERROR', 'QUOTA_EXCEEDED', 'DOCUMENT_AI_ERROR', 'UNSUPPORTED_TYPE', 'NO_TEXT_FOUND', 'EMPTY_FILE'].includes(key)
    ).reduce((sum, key) => sum + errorGroups[key].length, 0);
    
    if (otherErrors > 0) {
      messages.push(`${otherErrors} autre(s) erreur(s)`);
    }
    
    return messages.join(', ');
  }, []);

  // Define performOcrOnFile before processOcrQueue to avoid initialization errors
  const performOcrOnFile = useCallback(async (file, reportType = '') => {
    // Helper function to read file as data URL
    const readFileAsDataURL = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    // Helper function to read file as text
    const readFileAsText = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    };
    
    const { type, name } = file;

    try {
      // Plain text files: lire localement pour éviter upload inutile
      if (type === 'text/plain' || /\.txt$/i.test(name)) {
        const fileText = await readFileAsText(file);
        if (!fileText.trim()) throw new Error('Fichier texte vide');
        return { file, text: fileText };
      }

      // Tous les autres fichiers: envoyer au backend OCR
      const backendResult = await ocrService.extractText(file, reportType);
      const extractedText = backendResult.content || backendResult.text || '';

      if (!extractedText) throw new Error('Aucun texte');

      return { file, text: extractedText };
    } catch (error) {
      error.file = file;
      throw error;
    }
  }, [ocrService]);

  const processOcrQueue = useCallback(async (forcedQueue, reportType = '') => {
    if (ocrProcessingRef.current) return;
    ocrProcessingRef.current = true;
    setIsExtractingText(true);

    try {
      const localQueue = forcedQueue || [...ocrQueueRef.current];

      // Check airplane mode
      if (isGlobalAirplaneMode) {
        if (!pausedDueToAirplaneMode) {
          showNotification('Annexe en pause (mode avion activé).', 'info');
          setPausedDueToAirplaneMode(true);
        }
        return;
      }

      // Check internet connection
      if (!isOnline) {
        showNotification('Pas de connexion internet, annexe en pause...', 'warning');
        return;
      }

      if (localQueue.length === 0) return;

      // Set initial progress
      setProcessingProgress({ current: 0, total: localQueue.length });

      // Process all files in parallel with progress tracking
      const results = await Promise.allSettled(
        localQueue.map((file, index) => 
          performOcrOnFile(file, reportType)
            .then(result => {
              // Update progress
              setProcessingProgress(prev => ({ ...prev, current: prev.current + 1 }));
              return { ...result, index, status: 'fulfilled' };
            })
            .catch(error => {
              // Update progress even on error
              setProcessingProgress(prev => ({ ...prev, current: prev.current + 1 }));
              return { file, error, index, status: 'rejected' };
            })
        )
      );

      // Separate successful and failed results
      const successfulResults = [];
      const failedResults = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.status === 'fulfilled') {
          successfulResults.push(result.value);
        } else if (result.status === 'fulfilled' && result.value.status === 'rejected') {
          failedResults.push(result.value);
        } else {
          // This shouldn't happen, but handle it just in case
          log.error('Unexpected result format:', result);
        }
      });

      // Append all successful texts in order
      if (successfulResults.length > 0) {
        // Sort by original index to maintain order
        successfulResults.sort((a, b) => a.index - b.index);
        
        // Append texts
        successfulResults.forEach(({ text }) => {
          setAnnexText(prev => prev + `\nProchain annexe:\n${text}\n`);
        });

        // Call onOcrComplete once after all successful extractions
        if (onOcrComplete) onOcrComplete();
      }

      // Remove only the files that were successfully processed from the CURRENT queue.
      const successfulFilesSet = new Set(successfulResults.map(r => r.file));

      setOcrQueue(prevQueue => prevQueue.filter(file => !successfulFilesSet.has(file)));
      ocrQueueRef.current = ocrQueueRef.current.filter(file => !successfulFilesSet.has(file));

      // Handle failed files
      if (failedResults.length > 0) {
        if (failedResults.length === 1) {
          // Single error - use existing error handler
          const { file, error, index } = failedResults[0];
          handleOcrError(file, error, index);
        } else {
          // Multiple errors - handle as batch
          // Group errors by type
          const errorGroups = failedResults.reduce((acc, { file, error }) => {
            const errorType = categorizeError(error);
            if (!acc[errorType]) acc[errorType] = [];
            acc[errorType].push({ file, error });
            return acc;
          }, {});

          // Create batch error state
          const batchErrorState = {
            isBatch: true,
            failedFiles: failedResults,
            errorGroups,
            errorMessage: generateBatchErrorMessage(errorGroups),
            itemDescription: `${failedResults.length} fichiers ont échoué`
          };

          setOcrErrorDialogState(batchErrorState);
          ocrErrorDialogStateRef.current = batchErrorState;

          // Call parent error handler if provided
          if (onOcrError) {
            onOcrError({
              ...batchErrorState,
              onRetryAll: () => {
                // Clear error dialog
                setOcrErrorDialogState(null);
                ocrErrorDialogStateRef.current = null;
                // Add failed files back to queue
                const filesToRetry = failedResults.map(r => r.file);
                // Process them again
                if (!ocrProcessingRef.current && filesToRetry.length > 0 && processOcrQueueRef.current) {
                  processOcrQueueRef.current(filesToRetry, reportType);
                }
              },
              onDeleteAll: () => {
                // Clear error dialog
                setOcrErrorDialogState(null);
                ocrErrorDialogStateRef.current = null;
                // Files are already removed from queue, just continue processing if needed
                if (!ocrProcessingRef.current && ocrQueueRef.current.length > 0 && processOcrQueueRef.current) {
                  processOcrQueueRef.current(undefined, reportType);
                }
              },
              onRetry: () => {
                // Same as onRetryAll for batch
                setOcrErrorDialogState(null);
                ocrErrorDialogStateRef.current = null;
                const filesToRetry = failedResults.map(r => r.file);
                if (!ocrProcessingRef.current && filesToRetry.length > 0 && processOcrQueueRef.current) {
                  processOcrQueueRef.current(filesToRetry, reportType);
                }
              },
              onDelete: () => {
                // Same as onDeleteAll for batch
                setOcrErrorDialogState(null);
                ocrErrorDialogStateRef.current = null;
                if (!ocrProcessingRef.current && ocrQueueRef.current.length > 0 && processOcrQueueRef.current) {
                  processOcrQueueRef.current(undefined, reportType);
                }
              }
            });
          }
        }
      }
  
    } finally {
      ocrProcessingRef.current = false;
      setIsExtractingText(false);
      setProcessingProgress({ current: 0, total: 0 });
      // Automatically continue processing if new items were added to the queue during the previous run
      if (
        ocrQueueRef.current.length > 0 &&      // still items pending
        !ocrErrorDialogStateRef.current &&     // no error dialog shown (avoids infinite retries) - using ref
        processOcrQueueRef.current &&
        !ocrProcessingRef.current               // ensure not already running
      ) {
        processOcrQueueRef.current(undefined, reportType);
      }
    }
  }, [
    isOnline,
    showNotification,
    handleOcrError,
    performOcrOnFile,
    setAnnexText,
    onOcrComplete,
    categorizeError,
    generateBatchErrorMessage,
    onOcrError
    // ocrErrorDialogState removed to avoid infinite recursion (using ref instead)
  ]);

  // Assign processOcrQueue to ref to avoid circular dependencies
  useEffect(() => {
    processOcrQueueRef.current = processOcrQueue;
  }, [processOcrQueue]);

  // Resume OCR when airplane mode is disabled
  useEffect(() => {
    if (!isGlobalAirplaneMode && pausedDueToAirplaneMode) {
      setPausedDueToAirplaneMode(false);
      if (isOnline && ocrQueueRef.current.length > 0 && !ocrProcessingRef.current && processOcrQueueRef.current) {
        showNotification('Mode avion désactivé, reprise de l\'OCR...', 'success');
        processOcrQueueRef.current(undefined, selectedReportType);
      }
    }
  }, [isGlobalAirplaneMode, pausedDueToAirplaneMode, isOnline, selectedReportType, showNotification]);

  useEffect(() => {
    if (isOnline && !isGlobalAirplaneMode && ocrQueueRef.current.length > 0 && !ocrProcessingRef.current && processOcrQueueRef.current) {
      processOcrQueueRef.current(undefined, selectedReportType);
    }
  }, [isOnline, isGlobalAirplaneMode, selectedReportType]);

  // Effect to load OCR queue when draft changes
  useEffect(() => {
    if (currentDraftId) {
      loadOcrQueueForDraft(currentDraftId);
    } else {
      setOcrQueue([]);
    }
  }, [currentDraftId, loadOcrQueueForDraft]);

  // Effect to save OCR queue when it changes
  useEffect(() => {
    if (currentDraftId && ocrQueue.length > 0) {
      saveOcrQueueForDraft(currentDraftId);
    }
  }, [ocrQueue, currentDraftId, saveOcrQueueForDraft]);

  const clearOcrQueue = useCallback(() => {
    setOcrQueue([]);
    ocrQueueRef.current = [];
    ocrProcessingRef.current = false;
    setIsExtractingText(false);
  }, []);

  // OCR Error handling functions
  const handleOcrErrorRetry = useCallback((file) => {
    // Clear error dialog
    setOcrErrorDialogState(null);
    ocrErrorDialogStateRef.current = null;

    // Resume processing if not already running
    if (!ocrProcessingRef.current && ocrQueueRef.current.length > 0 && processOcrQueueRef.current) {
      processOcrQueueRef.current(undefined, selectedReportType);
    }
  }, [selectedReportType]);

  const handleOcrErrorDelete = useCallback((file) => {
    // Clear error dialog
    setOcrErrorDialogState(null);
    ocrErrorDialogStateRef.current = null;

    // Remove the file from queue
    const newQueue = ocrQueueRef.current.filter(f => f !== file);
    setOcrQueue(newQueue);
    ocrQueueRef.current = newQueue;

    // Continue processing remaining items if any
    if (!ocrProcessingRef.current && newQueue.length > 0 && processOcrQueueRef.current) {
      processOcrQueueRef.current(undefined, selectedReportType);
    }
  }, [selectedReportType]);

  // Define batch handlers that use processOcrQueueRef
  const handleBatchRetry = useCallback((failedResults) => {
    setOcrErrorDialogState(null);
    ocrErrorDialogStateRef.current = null;
    const filesToRetry = failedResults.map(r => r.file);
    if (!ocrProcessingRef.current && filesToRetry.length > 0 && processOcrQueueRef.current) {
      processOcrQueueRef.current(filesToRetry, selectedReportType);
    }
  }, [selectedReportType]);

  const handleBatchDelete = useCallback((failedResults) => {
    setOcrErrorDialogState(null);
    ocrErrorDialogStateRef.current = null;
    if (!ocrProcessingRef.current && ocrQueueRef.current.length > 0 && processOcrQueueRef.current) {
      processOcrQueueRef.current(undefined, selectedReportType);
    }
  }, [selectedReportType]);

  return {
    ocrQueue,                  // Array of File objects for airplane mode save
    ocrQueueLength: ocrQueue.length,
    isExtractingText,
    addFileToOcrQueue,
    processOcrQueue: (queue) => processOcrQueue(queue, selectedReportType),
    clearOcrQueue,
    ocrErrorDialogState,
    handleOcrErrorRetry: ocrErrorDialogState?.isBatch ? handleBatchRetry : handleOcrErrorRetry,
    handleOcrErrorDelete: ocrErrorDialogState?.isBatch ? handleBatchDelete : handleOcrErrorDelete,
    setOcrErrorDialogState,
    processingProgress,
    saveOcrQueueForDraft,
    loadOcrQueueForDraft,
    clearOcrQueueForDraft
  };
}